import React, { useState, useCallback } from 'react'
import { Box, Paper, Grid } from '@mui/material'
import NavigationPane from './NavigationPane'
import InspectorPane from './InspectorPane'

export interface InspectorLayoutProps {
  data: any
  onChange: (data: any) => void
  readonly?: boolean
}

export interface SelectedItem {
  type: 'main-segment' | 'loop' | 'segment' | 'element'
  id: string
  path: string[]
  data: any
}

const InspectorLayout = React.memo<InspectorLayoutProps>(({ data, onChange, readonly = false }) => {
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [navigationPaneVisible, setNavigationPaneVisible] = useState(true)

  const handleItemSelect = useCallback((item: SelectedItem) => {
    setSelectedItem(item)
  }, [])

  const handleItemExpand = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      newSet.add(itemId)
      return newSet
    })
  }, [])

  const handleItemCollapse = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      newSet.delete(itemId)
      return newSet
    })
  }, [])

  const handleInspectorChange = useCallback(
    (updatedData: any) => {
      onChange(updatedData)
    },
    [onChange]
  )

  const handleNavigateToItem = useCallback((item: SelectedItem) => {
    setSelectedItem(item)
  }, [])

  const handleToggleNavigationPane = useCallback(() => {
    setNavigationPaneVisible(prev => !prev)
  }, [])

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Grid
        container
        sx={{
          height: '100%',
          width: '100%',
          flex: 1,
          minHeight: 0, // Important for flex child to shrink
        }}
        spacing={1}
      >
        {/* Navigation Pane */}
        {navigationPaneVisible && (
          <Grid
            item
            xs={12}
            md={4}
            lg={4}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0, // Important for flex child to shrink
            }}
          >
            <Paper
              elevation={1}
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            >
              <NavigationPane
                data={data}
                selectedItem={selectedItem}
                expandedItems={expandedItems}
                onItemSelect={handleItemSelect}
                onItemExpand={handleItemExpand}
                onItemCollapse={handleItemCollapse}
                readonly={readonly}
              />
            </Paper>
          </Grid>
        )}

        {/* Inspector Pane */}
        <Grid
          item
          xs={12}
          md={navigationPaneVisible ? 8 : 12}
          lg={navigationPaneVisible ? 8 : 12}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0, // Important for flex child to shrink
          }}
        >
          <Paper
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <InspectorPane
              selectedItem={selectedItem}
              data={data}
              onChange={handleInspectorChange}
              onNavigateToItem={handleNavigateToItem}
              onToggleNavigationPane={handleToggleNavigationPane}
              navigationPaneVisible={navigationPaneVisible}
              readonly={readonly}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
})

InspectorLayout.displayName = 'InspectorLayout'

export default InspectorLayout
